import { useParams, useNavigate } from "react-router-dom";
import { Button, Typography, Empty, Modal, Skeleton, message } from "antd";
import { MaterialIcon } from "@/components/MaterialIcon";
import { useEffect, useState, useMemo } from "react";
import { collection, query, where, orderBy, onSnapshot, deleteDoc, doc } from "firebase/firestore";
import { db } from "@/config/firebase";
import { useAuthStore } from "@/store/authStore";
import { useEffectiveUserId } from "@/hooks/useEffectiveUserId";
import type { Photo, View, TimerSettings, CameraSettings, VideoJob } from "@/types";
import { logError } from "@/utils/logger";
import { convertPhotoUrls } from "@/utils/storage";
import {
  DownloadFormat,
  downloadSinglePhoto,
  downloadPhotosAsZip,
} from "@/utils/downloadUtils";
import { createManifestJob, ManifestResult } from "@/services/downloadService";
import { createVideoJob, subscribeToVideoJobs, getActiveJobsCount } from "@/services/videoService";
import { getProjectById } from "@/services/projectService";
import { PhotoViewer } from "@/components/PhotoViewer";
import { TestShotViewer } from "@/components/TestShotViewer";
import { GalleryGridMode } from "@/components/GalleryGridMode";
import { NetflixPlayer } from "@/features/player";
import { DownloadDialog } from "@/components/DownloadDialog";
import { ManifestDownloadDialog } from "@/components/ManifestDownloadDialog";
import { VideoConfig } from "@/components/CreateVideoDialog";
import { GenerateRangeVideoDialog } from "@/components/GenerateRangeVideoDialog";
import { usePhotoSelection } from "@/hooks/usePhotoSelection";
import { updateTimerSettings, updateCameraSettings, subscribeToView } from "@/services/viewService";
import AppLayout from "@/components/AppLayout";
import { useUserData } from "@/hooks/useUserData";
import { usePlansConfig } from "@/hooks/usePlansConfig";
import { useViewEntitlements } from "@/hooks/useViewEntitlements";
import { SettingsDrawer } from "@/components/SettingsDrawer";

const { Title } = Typography;

type ViewMode = 'grid' | 'player';

/**
 * Photos page - displays photos for a specific view
 * Phase 2: Gallery with GRID/PLAYER modes, selection system, batch actions
 */
export default function PhotosPage() {
  const { viewId } = useParams<{ viewId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { effectiveUid, realUid, isImpersonating } = useEffectiveUserId();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [view, setView] = useState<View | null>(null);
  const [loading, setLoading] = useState(true);
  // True once the view subscription has fired at least once (even with null)
  const [viewLoaded, setViewLoaded] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid'); // Default to GRID mode
  // Use photoId instead of index for navigation stability with real-time updates
  const [viewerState, setViewerState] = useState<{
    visible: boolean;
    photoId: string;
  } | null>(null);

  // Calculate current index dynamically from photoId
  const viewerCurrentIndex = viewerState
    ? photos.findIndex((p) => p.photoId === viewerState.photoId)
    : -1;

  // Selection system
  const {
    selectedIds,
    selectedPhotos,
    selectedCount,
    toggleSelection,
    selectMany,
    deselectMany,
    clearSelection,
    selectAll,
    isSelected,
  } = usePhotoSelection(photos);

  // Video resolution limit of THIS view. In the per-view model the tier lives
  // on the view's license, so reading it from the user (as this did) gives the
  // wrong answer for any account holding views on different tiers.
  const { userData } = useUserData();
  const { plansConfig } = usePlansConfig();
  const { get: getEntitlements } = useViewEntitlements();
  const entitlements = getEntitlements(viewId);
  const maxResolution = useMemo(() => {
    if (!userData) return "1080p" as const;
    if (userData.isAdmin) return "4k" as const;
    if (entitlements.licensed) return entitlements.maxResolution;
    // Pre-V3 accounts have no license at all: read the legacy V2 limits.
    if (!plansConfig) return "1080p" as const;
    const planTier = userData.plan?.tier || "free";
    return plansConfig.plans[planTier]?.limits?.maxResolution || "1080p";
  }, [userData, plansConfig, entitlements.licensed, entitlements.maxResolution]);

  // Reverse photos for player mode (oldest first, like video timeline)
  const photosForPlayer = useMemo(
    () => [...photos].reverse(),
    [photos]
  );

  const [settingsDrawerVisible, setSettingsDrawerVisible] = useState(false);
  const [testShotVisible, setTestShotVisible] = useState(false);

  // Video jobs state
  const [videoJobs, setVideoJobs] = useState<VideoJob[]>([]);
  const [projectName, setProjectName] = useState<string>("");
  const hasActiveVideoJob = useMemo(() => getActiveJobsCount(videoJobs) > 0, [videoJobs]);

  // Load view metadata with real-time updates (for drawer status)
  useEffect(() => {
    if (!viewId) return;

    const unsubscribe = subscribeToView(
      viewId,
      (v) => {
        setView(v);
        setViewLoaded(true);
      },
      (error) => {
        logError("Error fetching view", error, { viewId });
        setViewLoaded(true);
      },
      effectiveUid ?? undefined
    );

    return () => unsubscribe();
  }, [viewId, effectiveUid]);

  // Subscribe to video jobs
  useEffect(() => {
    if (!effectiveUid) return;

    const unsubscribe = subscribeToVideoJobs(effectiveUid, setVideoJobs);
    return () => unsubscribe();
  }, [effectiveUid]);

  // Load project name for video naming
  useEffect(() => {
    if (!effectiveUid || !view?.projectId) return;

    getProjectById(effectiveUid, view.projectId)
      .then((project) => {
        if (project) {
          setProjectName(project.name);
        }
      })
      .catch((error) => logError("Error fetching project", error));
  }, [effectiveUid, view?.projectId]);

  useEffect(() => {
    if (!effectiveUid || !viewId) return;

    setPhotos([]);
    setLoading(true);

    const photosQuery = query(
      collection(db, "photos"),
      where("userId", "==", effectiveUid),
      where("viewId", "==", viewId),
      orderBy("capturedAt", "desc")
    );

    // Render the first batch immediately so the user sees the gallery quickly,
    // then defer the rest to an idle callback. This avoids blocking the
    // initial paint on views with thousands of photos.
    const FIRST_RENDER_BATCH = 500;
    let idleHandle: number | null = null;

    const unsubscribe = onSnapshot(
      photosQuery,
      (snapshot) => {
        const photosList = snapshot.docs.map((doc) => {
          const data = {
            ...doc.data(),
            photoId: doc.id,
          } as any;

          // Convert gs:// URLs to HTTPS URLs
          return convertPhotoUrls(data);
        });

        if (photosList.length <= FIRST_RENDER_BATCH) {
          setPhotos(photosList);
          setLoading(false);
          return;
        }

        // First paint with the most recent N photos (already sorted desc).
        setPhotos(photosList.slice(0, FIRST_RENDER_BATCH));
        setLoading(false);

        // Append the rest when the browser is idle so the initial render
        // (Virtuoso + day grouping) is not blocked. requestIdleCallback is
        // not available on Safari iOS — fall back to setTimeout there.
        const ric: (cb: () => void) => number =
          (window as unknown as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback
            ? (cb) =>
                (window as unknown as { requestIdleCallback: (cb: () => void) => number }).requestIdleCallback(cb)
            : (cb) => window.setTimeout(cb, 0);
        if (idleHandle !== null) {
          const cic = (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback;
          if (cic) cic(idleHandle);
          else window.clearTimeout(idleHandle);
        }
        idleHandle = ric(() => {
          setPhotos(photosList);
          idleHandle = null;
        });
      },
      (error) => {
        logError("Error fetching photos", error, { viewId });
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
      if (idleHandle !== null) {
        const cic = (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback;
        if (cic) cic(idleHandle);
        else window.clearTimeout(idleHandle);
      }
    };
  }, [effectiveUid, viewId]);

  const handlePhotoClick = (index: number) => {
    // For player mode, convert index from reversed array to original
    // photosForPlayer is reversed, so index 0 in player = last item in photos
    const actualIndex = viewMode === 'player'
      ? photos.length - 1 - index
      : index;
    const photo = photos[actualIndex];
    if (photo) {
      setViewerState({ visible: true, photoId: photo.photoId });
    }
  };

  // Download single photo from PhotoViewer (JPEG only for backward compatibility)
  const handleDownloadSingle = async (photo: Photo) => {
    const result = await downloadSinglePhoto(photo, "jpeg");
    if (result.success) {
      message.success("Photo downloaded successfully");
    } else {
      message.error(result.error || "Failed to download photo");
    }
  };

  // Manifest download state
  const [manifestResult, setManifestResult] = useState<ManifestResult | null>(null);
  const [manifestDialogOpen, setManifestDialogOpen] = useState(false);
  const [rangeVideoDialogOpen, setRangeVideoDialogOpen] = useState(false);
  const [topDownloadDialogOpen, setTopDownloadDialogOpen] = useState(false);

  // Batch download with format selection (size-based routing)
  const handleBatchDownload = async (format: DownloadFormat) => {
    if (selectedCount === 1) {
      // Single photo: direct download
      const photo = selectedPhotos[0];
      const result = await downloadSinglePhoto(photo, format, (msg) =>
        message.loading({ content: msg, key: "download" })
      );
      if (result.success) {
        message.success({ content: "Photo downloaded successfully", key: "download" });
      } else {
        message.error({ content: result.error || "Failed to download photo", key: "download" });
      }
      clearSelection();
    } else {
      // Client-side ZIP (up to 1GB)
      message.loading({ content: "Preparing download...", key: "download", duration: 0 });

      const result = await downloadPhotosAsZip(
        selectedPhotos,
        format,
        view?.name || "photos",
        (loaded, total, msg) => {
          message.loading({
            content: `${msg} (${loaded}/${total})`,
            key: "download",
            duration: 0,
          });
        }
      );

      if (result.success) {
        message.success({ content: "Download complete!", key: "download" });
        clearSelection();
      } else {
        message.error({ content: result.error || "Failed to create ZIP", key: "download" });
      }
    }
  };

  // Manifest download for very large downloads (>4GB)
  const handleManifestDownload = async (format: DownloadFormat) => {
    if (!effectiveUid || !user || !viewId || !view?.name) {
      message.error("Unable to create download manifest");
      return;
    }

    try {
      message.loading({ content: "Generating download script...", key: "manifest", duration: 0 });

      const photoIds = selectedPhotos.map((p) => p.photoId);
      const idToken = await user.getIdToken();
      const result = await createManifestJob(
        effectiveUid,
        viewId,
        view.name,
        photoIds,
        format,
        idToken
      );

      message.destroy("manifest");
      setManifestResult(result);
      setManifestDialogOpen(true);
    } catch (error) {
      logError("Failed to create manifest", error as Error);
      message.error({
        content: "Failed to generate download script",
        key: "manifest",
      });
    }
  };

  const handleBatchDelete = async () => {
    try {
      // Delete from Firestore (R2 files cleaned by onPhotoDelete Cloud Function)
      await Promise.all(
        selectedPhotos.map((photo) => deleteDoc(doc(db, 'photos', photo.photoId)))
      );

      message.success(`${selectedCount} photo${selectedCount > 1 ? 's' : ''} deleted successfully`);
      clearSelection();
    } catch (error) {
      message.error('Failed to delete photos');
      logError('Batch delete error', error as Error);
    }
  };

  const handleShare = async (photo: Photo) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Photo',
          text: `Photo captured at ${photo.capturedAt?.toDate?.().toLocaleString()}`,
          url: photo.urls.full,
        });
      } else {
        await navigator.clipboard.writeText(photo.urls.full);
        message.success('Photo URL copied to clipboard');
      }
    } catch (error) {
      message.error('Failed to share photo');
    }
  };

  // Create video from selected photos
  const handleCreateVideo = async (config: VideoConfig) => {
    if (!effectiveUid || !viewId || !view) return;

    try {
      message.loading({ content: "Creating video job...", key: "video" });

      await createVideoJob(
        effectiveUid,
        view.projectId,
        projectName || "Timelapse",
        viewId,
        view.name,
        {
          resolution: config.resolution,
          fps: config.fps,
          aspectRatio: config.aspectRatio,
          photoIds: config.photoIds,
          photoCount: config.photoCount,
          dateRange: config.dateRange,
          ...(isImpersonating && realUid ? { adminCreated: true, createdBy: realUid } : {}),
        }
      );

      message.success({
        content: "Video generation started! Check the Videos page for progress.",
        key: "video",
        duration: 5,
      });
      clearSelection();
      navigate("/videos");
    } catch (error) {
      logError("Failed to create video job", error as Error);
      message.error({
        content: "Failed to create video job",
        key: "video",
      });
    }
  };

  // Save timer settings
  const handleSaveTimerSettings = async (
    capturing: boolean,
    timerSettings?: TimerSettings
  ) => {
    if (!effectiveUid || !view) return;

    try {
      await updateTimerSettings(
        effectiveUid,
        view.projectId,
        view.viewId,
        capturing,
        timerSettings
      );
    } catch (error) {
      console.error('Failed to save timer settings:', error);
      throw error;
    }
  };

  // Save camera settings
  const handleSaveCameraSettings = async (cameraSettings: CameraSettings) => {
    if (!effectiveUid || !view) return;

    try {
      await updateCameraSettings(
        effectiveUid,
        view.projectId,
        view.viewId,
        cameraSettings
      );
    } catch (error) {
      console.error('Failed to save camera settings:', error);
      throw error;
    }
  };

  // Single reveal: hold ONE skeleton until the view doc and the first photos
  // snapshot have both arrived — header, toolbar and gallery appear together.
  // Both are primary sources (no forced reveal); their subscriptions also
  // resolve on error. Incremental loading of the photo THUMBNAILS after
  // reveal is intentional.
  const pageReady = viewLoaded && !loading;

  if (!pageReady) {
    return (
      <AppLayout>
        {/* Skeleton mirrors the real header + gallery grid so the layout
            does not jump at reveal */}
        <div className="mb-6 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <Button
              icon={<MaterialIcon name="arrow_back" />}
              onClick={() => navigate("/")}
            />
            <Skeleton
              active
              title={false}
              paragraph={{ rows: 1, width: 160 }}
              style={{ margin: 0, maxWidth: 200 }}
            />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button.Group className="view-mode-switcher">
              <Button
                type="primary"
                icon={<MaterialIcon name="grid_view" size={24} style={{ marginTop: -3 }} />}
              />
              <Button
                icon={<MaterialIcon name="video_label" size={24} style={{ marginTop: -3 }} />}
              />
            </Button.Group>
          </div>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 8,
          }}
        >
          {Array.from({ length: 18 }).map((_, i) => (
            <div
              key={i}
              style={{ aspectRatio: '4 / 3', background: '#f0f0f0', borderRadius: 4 }}
            />
          ))}
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Page Header - wraps to a second row on narrow viewports */}
      <div className="mb-6 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <Button
            icon={<MaterialIcon name="arrow_back" />}
            onClick={() => navigate("/")}
          />
          {selectedCount > 0 ? (
            <>
              <Title level={4} style={{ margin: 0, minWidth: 0, flex: 1 }} ellipsis className="hidden md:block">
                {view?.name || 'Loading...'}
              </Title>
              <Button
                type="link"
                size="small"
                danger
                style={{ fontWeight: 400, fontSize: 14, padding: 0, flexShrink: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  clearSelection();
                }}
              >
                {`Unselect (${selectedCount})`}
              </Button>
            </>
          ) : (
            <>
              <Title level={4} style={{ margin: 0, minWidth: 0, flex: 1 }} ellipsis>
                {view?.name || 'Loading...'}
              </Title>
              {!loading && photos.length > 0 && (
                <Button
                  type="link"
                  size="small"
                  className="hidden md:inline-flex"
                  style={{ fontWeight: 400, fontSize: 14, padding: 0, flexShrink: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectAll();
                  }}
                >
                  Select all
                </Button>
              )}
            </>
          )}
        </div>

        {/* View Mode Switcher + Settings - shrinks on mobile (icon-only Download) */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button.Group className="view-mode-switcher">
            <Button
              type={viewMode === 'grid' ? 'primary' : 'default'}
              icon={<MaterialIcon name="grid_view" size={24} style={{ marginTop: -3 }} />}
              onClick={() => setViewMode('grid')}
            />
            <Button
              type={viewMode === 'player' ? 'primary' : 'default'}
              icon={<MaterialIcon name="video_label" size={24} style={{ marginTop: -3 }} />}
              onClick={() => setViewMode('player')}
            />
          </Button.Group>
          {!loading && photos.length > 0 && (
            <Button
              icon={<MaterialIcon name="movie_filter" size={24} />}
              onClick={() => setRangeVideoDialogOpen(true)}
              title="Generate video"
            />
          )}
          {selectedCount > 0 && (
            <>
              <Button
                icon={<MaterialIcon name="download" size={24} />}
                onClick={() => setTopDownloadDialogOpen(true)}
                title="Download"
              />
              <Button
                icon={<MaterialIcon name="delete" size={24} />}
                danger
                onClick={() => {
                  Modal.confirm({
                    title: "Delete photos?",
                    content: `You are about to delete ${selectedCount} photo${selectedCount > 1 ? "s" : ""}. This action cannot be undone.`,
                    okText: "Delete",
                    okType: "danger",
                    cancelText: "Cancel",
                    onOk: handleBatchDelete,
                  });
                }}
                title="Delete"
              />
            </>
          )}
        </div>
      </div>

      {/* Photos Gallery — pageReady guarantees the first snapshot arrived */}
      {photos.length === 0 ? (
        <Empty description="No photos yet. Upload your first photo from the Android app!" />
      ) : (
        <>
          {/* GRID Mode */}
          {viewMode === 'grid' && (
            <GalleryGridMode
              photos={photos}
              loading={loading}
              selectedIds={selectedIds}
              onPhotoClick={handlePhotoClick}
              onSelectionToggle={toggleSelection}
              onSelectMany={selectMany}
              onDeselectMany={deselectMany}
              isSelected={isSelected}
              timezone={view?.androidStatus?.device?.timezone}
            />
          )}

          {/* PLAYER Mode - uses reversed photos for video-like timeline (oldest left) */}
          {viewMode === 'player' && (
            <NetflixPlayer
              photos={photosForPlayer}
              loading={loading}
              selectedIds={selectedIds}
              onPhotoClick={handlePhotoClick}
              onSelectionToggle={toggleSelection}
              isSelected={isSelected}
              timezone={view?.androidStatus?.device?.timezone}
            />
          )}
        </>
      )}

      {/* Generate video by date-range dialog */}
      <GenerateRangeVideoDialog
        open={rangeVideoDialogOpen}
        photos={photos}
        selectedPhotos={selectedPhotos}
        hasActiveJob={hasActiveVideoJob}
        maxResolution={maxResolution}
        onCreateVideo={async (config) => {
          await handleCreateVideo(config);
          setRangeVideoDialogOpen(false);
        }}
        onCancel={() => setRangeVideoDialogOpen(false)}
      />

      {/* PhotoViewer Lightbox */}
      {viewerState && viewerCurrentIndex >= 0 && (
        <PhotoViewer
          photo={photos[viewerCurrentIndex]}
          photos={photos}
          currentIndex={viewerCurrentIndex}
          showFullMetadata={true}
          timezone={view?.androidStatus?.device?.timezone}
          onClose={() => setViewerState(null)}
          onNavigate={(newIndex) => {
            const newPhoto = photos[newIndex];
            if (newPhoto) {
              setViewerState({ visible: true, photoId: newPhoto.photoId });
            }
          }}
          onShare={() => handleShare(photos[viewerCurrentIndex])}
          onDownload={() => handleDownloadSingle(photos[viewerCurrentIndex])}
        />
      )}

      <DownloadDialog
        open={topDownloadDialogOpen}
        photos={selectedPhotos}
        onDownload={(format) => {
          setTopDownloadDialogOpen(false);
          handleBatchDownload(format);
        }}
        onManifestDownload={(format) => {
          setTopDownloadDialogOpen(false);
          handleManifestDownload(format);
        }}
        onCancel={() => setTopDownloadDialogOpen(false)}
      />

      <SettingsDrawer
        open={settingsDrawerVisible}
        view={view ?? null}
        onClose={() => {
          setSettingsDrawerVisible(false);
        }}
        onTestShot={() => setTestShotVisible(true)}
        onSaveCameraSettings={handleSaveCameraSettings}
        onSaveTimerSettings={handleSaveTimerSettings}
      />

      {/* Test Shot Viewer */}
      {testShotVisible && view && (
        <TestShotViewer
          view={view}
          onClose={() => setTestShotVisible(false)}
        />
      )}

      {/* Manifest Download Dialog (Level 2: >4GB) */}
      {manifestResult && (
        <ManifestDownloadDialog
          open={manifestDialogOpen}
          manifestResult={manifestResult}
          onClose={() => {
            setManifestDialogOpen(false);
            setManifestResult(null);
            clearSelection();
          }}
        />
      )}
    </AppLayout>
  );
}
